import { queryOptions, type QueryClient } from '@tanstack/react-query'
import {
  OrganizationBySlugDocument,
  OrganizationSlugsDocument,
  type OrganizationBySlugQuery,
} from '@/graphql/generated'
import { graphqlRequest } from '@/lib/api/graphql'
import { keys } from '@/lib/queries/keys'
import { nodes } from '@/lib/queries/mappers'
import type { Organization } from '@/lib/types'

/**
 * The organization a URL points at, whether or not you're a member of it.
 *
 * Deliberately separate from the session query. That one answers "which
 * workspaces can I switch to" — a membership question — and using it to answer
 * "which workspace is /o/<slug> about" silently excluded every organization an
 * administrator reaches through IAM rather than a membership row.
 *
 * Returns `null` for a slug that resolves to nothing, which covers both "no
 * such organization" and "RLS says this caller may not see it". The two are
 * indistinguishable here on purpose: telling them apart would confirm the
 * existence of an organization to someone with no access to it.
 */
export function selectOrganization(
  data: OrganizationBySlugQuery,
): Organization | null {
  const node = nodes(data.organizationsCollection)[0]
  if (!node) return null

  return {
    id: node.id,
    slug: node.slug,
    name: node.name,
    plan: node.organizationType?.name ?? 'Personal Account',
    planCode: node.organizationType?.code ?? 'personal',
    memberCount: nodes(node.organizationMembersCollection).length,
  }
}

export function organizationBySlugQuery(slug: string) {
  return queryOptions({
    queryKey: keys.organizationBySlug(slug),
    queryFn: ({ signal }) =>
      graphqlRequest(OrganizationBySlugDocument, { slug }, { signal }),
    /* Same reasoning as the session query: an organization's name and plan
       change about as often as its membership does. */
    staleTime: 5 * 60_000,
    select: selectOrganization,
  })
}

/** The mapped organization, for Server Components. See `fetchSession`. */
export async function fetchOrganizationBySlug(
  queryClient: QueryClient,
  slug: string,
): Promise<Organization | null> {
  return selectOrganization(
    await queryClient.fetchQuery(organizationBySlugQuery(slug)),
  )
}

/**
 * Slug and name for a set of organization ids.
 *
 * Search hits carry an `organizationId` but no slug, and every route needs the
 * slug. The session covers only organizations you are a MEMBER of, so this fills
 * the gap for the ones an administrator reaches through IAM — without it their
 * cross-organization hits were silently unroutable and dropped from the palette.
 *
 * Keyed on the sorted id list so two searches that surface the same
 * organizations share one cache entry rather than refetching per keystroke.
 */
export function organizationSlugsQuery(ids: string[]) {
  const unique = [...new Set(ids)].sort()

  return queryOptions({
    queryKey: ['organizations', 'slugs', unique],
    queryFn: ({ signal }) =>
      graphqlRequest(OrganizationSlugsDocument, { ids: unique }, { signal }),
    /* Only worth asking once there is something to ask about. */
    enabled: unique.length > 0,
    /* Slugs are read-only in the UI and change roughly never. */
    staleTime: 10 * 60_000,
    select: (data): Record<string, { slug: string; name: string }> => {
      const byId: Record<string, { slug: string; name: string }> = {}
      for (const node of nodes(data.organizationsCollection)) {
        byId[node.id] = { slug: node.slug, name: node.name }
      }
      return byId
    },
  })
}
